import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, ExternalLink } from "lucide-react";
import { GoldBadge, Bullets, Reveal, MaskedLines, Marquee } from "@/components/shared";
import VolunteerSection from "@/components/VolunteerSection";

const BRANCHES = {
  "predator-prevention-team": {
    code: "BR-01",
    short: "PPT",
    titleLines: ["Predator", "Prevention Team"],
    tagline: "Eyes on the street. Boots on the asphalt.",
    accent: "#EF4444",
    image: "/assets/ppt-ribbon.jpg",
    imageAlt: "Predator Prevention Team red and blue ribbon",
    imageContain: true,
    intro:
      "Direct street-level car and foot patrols running community safety initiatives to deter threats on the asphalt — before they reach a child's door.",
    marquee: ["Street Patrols", "Visible Deterrence", "Rapid Reporting", "Community Watch", "Boots On The Asphalt"],
    sections: [
      {
        title: "What The Patrols Do",
        bullets: [
          "Street-level car and foot patrols across high-risk community zones",
          "Visible deterrence of predatory behaviour in public spaces",
          "Documented observation and escalation protocols with authorities",
          "Community safety initiatives run in partnership with local residents",
        ],
      },
      {
        title: "How A Patrol Runs",
        bullets: [
          "Mapped routes through areas where risk is escalating",
          "Trained volunteers operating in pairs — never alone",
          "Every shift logged, every incident escalated to police where required",
          "Consistent presence: the same streets, the same faces, night after night",
        ],
      },
      {
        title: "Why It Works",
        bullets: [
          "Predators avoid streets that are watched",
          "A visible presence lowers the opportunity for harm before it starts",
          "Residents report feeling safer where patrols run",
          "Every patrol feeds the fight against rising child abuse statistics",
        ],
      },
    ],
    cta: { label: "Report a Safety Concern", to: "/contact" },
  },
  "soldiers-of-pain": {
    code: "BR-02",
    short: "S.O.P.",
    titleLines: ["Soldiers of", "P.A.I.N."],
    tagline: "A brotherhood and sisterhood forged in survival.",
    accent: "#EAB308",
    image: "/assets/soldiers-ribbon.jpg",
    imageAlt: "Soldiers of P.A.I.N. yellow and blue ribbon",
    imageContain: true,
    intro:
      "A dedicated social and community club focused intensely on child abuse prevention, mental health awareness, and suicide prevention.",
    marquee: ["Community Club", "Mental Health Awareness", "Suicide Prevention", "We Listen, We Believe, We Act"],
    sections: [
      {
        title: "The Club",
        bullets: [
          "Membership open to survivors, families, and allies",
          "Regular gatherings built on lived experience, not judgement",
          "A standing network of people who actually answer the phone",
        ],
      },
      {
        title: "Our Focus",
        bullets: [
          "Child abuse prevention campaigns and public awareness drives",
          "Mental health awareness programs led by lived-experience members",
          "Suicide prevention outreach and peer-support networks",
        ],
      },
      {
        title: "Join The Ranks",
        bullets: [
          "No fees, no paperwork — just a conversation",
          "Stand with us at patrols, events, and community drives",
          "When a child speaks, we listen, we believe, we act",
        ],
      },
    ],
    cta: { label: "Join The Soldiers", to: "/contact" },
  },
  "broken-crayons": {
    code: "BR-03",
    short: "Youth Support",
    titleLines: ["Broken", "Crayons"],
    tagline: "Broken pieces can still draw beautiful futures.",
    accent: "#2DD4BF",
    image: "/assets/broken-crayons-logo.jpg",
    imageAlt: "Broken Crayons Still Colour logo",
    imageContain: true,
    appUrl: "https://safety-first-165.preview.emergentagent.com",
    intro:
      "A specialized initiative targeting early childhood and youth support — meeting young people where they are, before the damage sets in.",
    marquee: ["Early Childhood Support", "Youth Mentoring", "Creative Recovery", "Still Colour"],
    sections: [
      {
        title: "Who We Stand Beside",
        bullets: [
          "Early childhood and youth support programs for at-risk young people",
          "Children who have been silenced, overlooked, or written off",
          "Families who need a safe first step",
        ],
      },
      {
        title: "The Programs",
        bullets: [
          "Trauma-informed mentoring and creative recovery activities",
          "Safe spaces where children are heard, believed, and protected",
          "Pathways from crisis into education, community, and stability",
        ],
      },
      {
        title: "The Broken Crayons App",
        bullets: [
          "A dedicated digital front door for young people",
          "Tools, support, and safe contact in one place",
          "Built mobile-first — because that is where young people live",
        ],
      },
    ],
    cta: { label: "Get Youth Support", to: "/contact" },
  },
  "born-from-broken": {
    code: "BR-04",
    short: "Official Campaign",
    titleLines: ["Born From", "Broken"],
    tagline: "From state ward to Royal Commission victory.",
    accent: "#3B82F6",
    image: "/assets/bfb-cover.webp",
    imageAlt: "Born From Broken — Rebirth From The Rubble: From Victim To Victorious, by Ron Waters",
    imageContain: false,
    intro:
      "The official autobiographical campaign, media portal, and anthem hub mapping Ronald Waters' 45-year journey from state ward to historical Royal Commission victory.",
    marquee: ["The Autobiography", "The Anthems", "45 Years On Record", "Rebirth From The Rubble"],
    sections: [
      {
        title: "The Book",
        bullets: [
          "The complete 45-year account, from state ward to national record",
          "Rebirth From The Rubble: From Victim To Victorious",
          "Limited first-edition pre-order with signed copy option",
          "All proceeds fund P.A.I.N. frontline programs",
        ],
      },
      {
        title: "The Anthems",
        bullets: [
          "When a Child Speaks — the anthem of the movement",
          "Born From Broken — the title track of the campaign",
          "Architect of Ruins — the sound of what was survived",
        ],
      },
      {
        title: "The Record",
        bullets: [
          "A private Royal Commission session held in Adelaide on 31 May 2017",
          "45 years of institutional data permanently entered into the nation's records",
          "Formal apologies forced from cross-continental institutions",
        ],
      },
    ],
    cta: { label: "Pre-Order & Stream", to: "/contact" },
  },
};

export default function BranchPage() {
  const { slug } = useParams();
  const branch = BRANCHES[slug];
  if (!branch) return <Navigate to="/initiatives" replace />;
  const a = branch.accent;

  return (
    <div data-testid="branch-page">
      <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, transparent, ${a}, transparent)` }} />

      <section
        className="relative overflow-hidden grain-overlay border-b border-white/10"
        style={{ background: `radial-gradient(ellipse at 85% 0%, ${a}24 0%, transparent 55%), #111622` }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 grid gap-12 lg:grid-cols-[1.2fr_1fr] lg:items-center">
          <div>
            <Reveal>
              <GoldBadge testId="branch-badge">
                {branch.code} — {branch.short}
              </GoldBadge>
            </Reveal>
            <h1 className="font-display mt-5 text-[11.5vw] sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] text-slate-50">
              <MaskedLines lines={branch.titleLines} />
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6 }}
              className="font-display mt-4 text-lg sm:text-xl italic"
              style={{ color: a }}
            >
              "{branch.tagline}"
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.75 }}
              className="mt-5 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
            >
              {branch.intro}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.9 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <Link
                to={branch.cta.to}
                data-testid="branch-cta-button"
                className="btn-royal inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
              >
                {branch.cta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              {branch.appUrl && (
                <a
                  href={branch.appUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="branch-app-link"
                  className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
                >
                  Open the Broken Crayons App
                  <ExternalLink className="h-4 w-4" />
                </a>
              )}
            </motion.div>
          </div>
          <motion.div
            initial={{ opacity: 0, scale: 0.94, rotate: 2 }}
            animate={{ opacity: 1, scale: 1, rotate: 0 }}
            transition={{ duration: 1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="spotlight-frame relative rounded-xl overflow-hidden border shadow-2xl max-w-md mx-auto lg:max-w-none w-full"
            style={{ borderColor: `${a}66`, boxShadow: `0 30px 60px -25px ${a}55` }}
          >
            <img
              src={branch.image}
              alt={branch.imageAlt}
              data-testid="branch-artwork"
              className={`w-full ${
                branch.imageContain ? "h-72 sm:h-96 object-contain bg-white/95 p-4" : "h-72 sm:h-96 object-cover object-right"
              }`}
            />
          </motion.div>
        </div>
      </section>

      <Marquee items={branch.marquee} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-24 space-y-16 sm:space-y-20">
        {branch.sections.map((s, i) => (
          <Reveal key={s.title} delay={0.05}>
            <div
              data-testid={`branch-section-${i + 1}`}
              className={`relative sm:max-w-2xl ${i % 2 === 1 ? "sm:ml-auto" : ""}`}
            >
              <span className="ghost-numeral absolute -top-12 sm:-top-16 left-0" aria-hidden="true">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h2 className="font-display relative text-xl sm:text-2xl font-medium tracking-wide" style={{ color: a }}>
                {s.title}
              </h2>
              <div
                className="h-[2px] w-16 my-4"
                style={{ background: `linear-gradient(90deg, ${a}, transparent)` }}
              />
              <Bullets items={s.bullets} />
            </div>
          </Reveal>
        ))}
      </section>

      <VolunteerSection branchName={branch.titleLines.join(" ")} accent={a} />

      <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-20 sm:pb-28">
        <Reveal>
          <div
            data-testid="branch-access-card"
            className="card-pain relative overflow-hidden p-8 sm:p-12 text-center"
            style={{ borderColor: `${a}55` }}
          >
            <div
              className="absolute inset-x-0 top-0 h-[2px]"
              style={{ background: `linear-gradient(90deg, transparent, ${a}, transparent)` }}
            />
            <h2 className="font-display text-2xl sm:text-3xl font-semibold text-slate-50">
              Access this branch through the Survivor Safe Portal
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-sm sm:text-base text-slate-300 leading-relaxed">
              Confidential, survivor-led, and answered by a trained advocate — never an automated
              system. You may remain anonymous until you choose otherwise.
            </p>
            <div className="mt-8 flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                data-testid="branch-portal-button"
                className="btn-royal inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-white"
              >
                {branch.cta.label}
                <ArrowRight className="h-4 w-4" />
              </Link>
              <Link
                to="/initiatives"
                data-testid="branch-back-link"
                className="btn-gold-outline inline-flex items-center gap-2 rounded-md px-6 py-3 text-sm font-semibold text-amber-300"
              >
                <ArrowLeft className="h-4 w-4" />
                All Branches
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
